'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Lang = 'EN' | 'TH';
export type Currency = 'USD' | 'THB';

const LANG_KEY = 'glanz_lang';
const CURRENCY_KEY = 'glanz_currency';

/* ───────────────────────────────────────────────────────────────────────────
 * Translation dictionary — one flat key → {en, th} map shared by the shell
 * and every page. t('key') returns the active language's string; missing keys
 * fall back to English, unknown keys fall back to the key itself.
 * ──────────────────────────────────────────────────────────────────────── */

// Shared dictionary. Also consumed by AutoTranslate (reverse en→th map) so
// every wired translation doubles as an automatic one for unwired pages.
export const DICT: Record<string, { en: string; th: string }> = {
  // Dashboard
  'dashboard.operationsOverview': { en: 'Operations Overview', th: 'ภาพรวมการดำเนินงาน' },
  'dashboard.asOf': { en: 'As of', th: 'ข้อมูล ณ' },
  'dashboard.lastRefresh': { en: 'Last refresh', th: 'อัปเดตล่าสุด' },
  'dashboard.liveData': { en: 'Live data', th: 'ข้อมูลสด' },
  'dashboard.synced': { en: 'Synced', th: 'ซิงค์แล้ว' },
  'dashboard.totalInventoryValue': { en: 'TOTAL INVENTORY VALUE', th: 'มูลค่าสินค้าคงเหลือรวม' },
  'dashboard.availableToSell': { en: 'AVAILABLE TO SELL', th: 'พร้อมขาย' },
  'dashboard.diamondsInStock': { en: 'diamonds in stock', th: 'เม็ดในสต็อก' },
  'dashboard.diamonds': { en: 'diamonds', th: 'เม็ด' },
  'dashboard.revenueMtd': { en: 'REVENUE MTD', th: 'รายได้เดือนนี้' },
  'dashboard.avgMargin': { en: 'Avg margin', th: 'มาร์จิ้นเฉลี่ย' },
  'dashboard.pendingPayments': { en: 'PENDING PAYMENTS', th: 'การชำระเงินที่ค้างชำระ' },
  'dashboard.overdueInvoice': { en: 'overdue invoice', th: 'ใบแจ้งหนี้เกินกำหนด' },
  'dashboard.overdueInvoices': { en: 'overdue invoices', th: 'ใบแจ้งหนี้เกินกำหนด' },
  'dashboard.onMemo': { en: 'ON MEMO', th: 'เมโม' },
  'dashboard.overdueMemos': { en: 'OVERDUE MEMOS', th: 'เมโมเกินกำหนด' },
  'dashboard.urgentRecovery': { en: 'urgent recovery needed', th: 'ต้องติดตามเร่งด่วน' },
  'dashboard.activeHolds': { en: 'ACTIVE HOLDS', th: 'การจองที่ใช้งาน' },
  'dashboard.reservedStones': { en: 'reserved stones', th: 'เม็ดที่ถูกจอง' },
  'dashboard.ytdRevenue': { en: 'YTD REVENUE', th: 'รายได้สะสมปีนี้' },
  'dashboard.yearToDate': { en: 'year-to-date', th: 'ตั้งแต่ต้นปี' },
  'dashboard.revenueMarginTrend': { en: 'Revenue & Margin Trend', th: 'แนวโน้มรายได้และมาร์จิ้น' },
  'dashboard.revenue': { en: 'Revenue', th: 'รายได้' },
  'dashboard.margin': { en: 'Margin', th: 'มาร์จิ้น' },
  'dashboard.inventoryByShape': { en: 'Inventory by Shape', th: 'สินค้าตามรูปเรือน' },
  'dashboard.totalStones': { en: 'total stones', th: 'เม็ดทั้งหมด' },
  'dashboard.statusDistribution': { en: 'Status Distribution', th: 'การกระจายสถานะ' },
  'dashboard.currentInventoryBreakdown': {
    en: 'Current inventory breakdown',
    th: 'ภาพรวมสต็อกปัจจุบัน',
  },
  'dashboard.caratVsPrice': { en: 'Carat vs Price', th: 'เนื้อหา vs ราคา' },
  'dashboard.carat': { en: 'Carat', th: 'เนื้อหา' },
  'dashboard.priceDistribution': {
    en: 'Price distribution by carat weight',
    th: 'การกระจายราคาตามน้ำหนัก',
  },
  'dashboard.alerts': { en: 'Alerts', th: 'การแจ้งเตือน' },
  'dashboard.markAllRead': { en: 'Mark all read', th: 'ทำเครื่องหมายว่าอ่านแล้ว' },
  'dashboard.activeMemos': { en: 'Active Memos', th: 'เมโมที่ใช้งาน' },
  'dashboard.dispatched': { en: 'dispatched', th: 'ส่งแล้ว' },
  'dashboard.active': { en: 'Active', th: 'ใช้งาน' },
  'dashboard.overdue': { en: 'Overdue', th: 'เกินกำหนด' },
  'dashboard.due': { en: 'Due', th: 'ครบกำหนด' },
  'dashboard.recentActivity': { en: 'Recent Activity', th: 'กิจกรรมล่าสุด' },
  'dashboard.events': { en: 'events', th: 'เหตุการณ์' },
  'dashboard.vsLastWeek': { en: 'vs last week', th: 'เทียบสัปดาห์ก่อน' },
  'dashboard.noChange': { en: 'no change', th: 'ไม่เปลี่ยนแปลง' },
  'dashboard.actionNeeded': { en: 'action needed', th: 'ต้องดำเนินการ' },

  // Holds
  'holds.title': { en: 'Hold Management', th: 'จัดการการจอง' },
  'holds.newHold': { en: 'New Hold', th: 'สร้างการจอง' },
  'holds.totalHolds': { en: 'Total Holds', th: 'การจองทั้งหมด' },
  'holds.activeHolds': { en: 'Active Holds', th: 'การจองที่ใช้งาน' },
  'holds.expiringSoon': { en: 'Expiring Soon', th: 'ใกล้หมดอายุ' },
  'holds.released': { en: 'Released', th: 'ปลดแล้ว' },
  'holds.heldValue': { en: 'held value', th: 'มูลค่าที่จอง' },
  'holds.within48h': { en: 'Within 48 hours', th: 'ภายใน 48 ชั่วโมง' },
  'holds.returnedToInventory': { en: 'Returned to inventory', th: 'คืนเข้าคลังแล้ว' },
  'holds.releasedCount': { en: 'released', th: 'ปลดแล้ว' },
  'holds.searchPlaceholder': {
    en: 'Search SKU, customer, shape...',
    th: 'ค้นหา SKU ลูกค้า รูปทรง...',
  },
  'holds.expiring48': { en: 'expiring within 48 hours', th: 'หมดอายุภายใน 48 ชั่วโมง' },
  'holds.contactConfirm': {
    en: 'contact customers to confirm or release',
    th: 'ติดต่อลูกค้าเพื่อยืนยันหรือปลดการจอง',
  },
  'holds.customer': { en: 'Customer', th: 'ลูกค้า' },
  'holds.shape': { en: 'Shape', th: 'รูปทรง' },
  'holds.carat': { en: 'Carat', th: 'กะรัต' },
  'holds.colorClarity': { en: 'Color/Clarity', th: 'สี/ความใส' },
  'holds.price': { en: 'Price', th: 'ราคา' },
  'holds.holdDate': { en: 'Hold Date', th: 'วันที่จอง' },
  'holds.expiryDate': { en: 'Expiry Date', th: 'วันหมดอายุ' },
  'holds.actions': { en: 'Actions', th: 'จัดการ' },
  'holds.release': { en: 'Release', th: 'ปลดการจอง' },
  'holds.expiresToday': { en: 'Expires today', th: 'หมดอายุวันนี้' },
  'holds.expired': { en: 'Expired', th: 'หมดอายุแล้ว' },
  'holds.daysLeft': { en: 'days left', th: 'วันที่เหลือ' },
  'holds.noHolds': { en: 'No holds found', th: 'ไม่พบการจอง' },
  'holds.placeHoldHint': {
    en: 'Place a hold on a diamond to track it here',
    th: 'สร้างการจองเพชรเพื่อติดตามที่นี่',
  },
  'holds.loadingHolds': { en: 'Loading holds…', th: 'กำลังโหลดการจอง…' },
  'holds.releaseConfirmTitle': { en: 'Release Hold', th: 'ปลดการจอง' },
  'holds.all': { en: 'All', th: 'ทั้งหมด' },
  'holds.active': { en: 'ACTIVE', th: 'ใช้งาน' },
  'holds.releasedTab': { en: 'RELEASED', th: 'ปลดแล้ว' },

  // Appraisal
  'apr.title': { en: 'Appraisal Workflow', th: 'เวิร์กโฟลว์การประเมิน' },
  'apr.flow': {
    en: 'Request → Assign Appraiser → Generate Report',
    th: 'ขอประเมิน → มอบหมาย → ออกรายงาน',
  },
  'apr.newRequest': { en: 'New Request', th: 'สร้างคำขอ' },
  'apr.all': { en: 'All', th: 'ทั้งหมด' },
  'apr.requested': { en: 'Requested', th: 'ร้องขอแล้ว' },
  'apr.assigned': { en: 'Assigned', th: 'มอบหมายแล้ว' },
  'apr.inProgress': { en: 'In Progress', th: 'กำลังดำเนินการ' },
  'apr.completed': { en: 'Completed', th: 'เสร็จสิ้น' },
  'apr.cancelled': { en: 'Cancelled', th: 'ยกเลิก' },
  'apr.searchPlaceholder': {
    en: 'Search by request #, SKU, or customer…',
    th: 'ค้นหาด้วยเลขคำขอ SKU หรือลูกค้า…',
  },
  'apr.requestNo': { en: 'Request #', th: 'เลขคำขอ' },
  'apr.diamond': { en: 'Diamond', th: 'เพชร' },
  'apr.requestedBy': { en: 'Requested By', th: 'ผู้ขอประเมิน' },
  'apr.purpose': { en: 'Purpose', th: 'วัตถุประสงค์' },
  'apr.appraiser': { en: 'Appraiser', th: 'ผู้ประเมิน' },
  'apr.estValue': { en: 'Est. Value', th: 'มูลค่าประเมิน' },
  'apr.unassigned': { en: 'Unassigned', th: 'ยังไม่มอบหมาย' },
  'apr.assign': { en: 'Assign', th: 'มอบหมาย' },
  'apr.start': { en: 'Start', th: 'เริ่ม' },
  'apr.complete': { en: 'Complete', th: 'เสร็จสิ้น' },
  'apr.assignAppraiser': { en: 'Assign Appraiser', th: 'มอบหมายผู้ประเมิน' },
  'apr.newAppraisalRequest': { en: 'New Appraisal Request', th: 'คำขอประเมินใหม่' },
  'apr.diamondSku': { en: 'Diamond SKU', th: 'รหัสเพชร' },
  'apr.available': { en: 'Available:', th: 'ที่มี:' },
  'apr.customerOrInternal': { en: 'Customer or internal name', th: 'ชื่อลูกค้าหรือภายใน' },
  'apr.notes': { en: 'Notes', th: 'บันทึก' },
  'apr.createRequest': { en: 'Create Request', th: 'สร้างคำขอ' },
  'apr.assigningFor': { en: 'Assigning appraiser for', th: 'มอบหมายผู้ประเมินสำหรับ' },
  'apr.appraisedValue': { en: 'Appraised Value (USD)', th: 'มูลค่าที่ประเมิน (USD)' },
  'apr.reportNotes': { en: 'Report Notes', th: 'บันทึกรายงาน' },
  'apr.reportPlaceholder': {
    en: 'Appraisal findings and methodology…',
    th: 'ผลการประเมินและวิธีการ…',
  },
  'apr.reportAttach': { en: 'Report will be attached to diamond', th: 'รายงานจะแนบกับเพชร' },
  'apr.completeReport': { en: 'Complete Appraisal Report', th: 'กรอกบันทึกการประเมิน' },
  'apr.completeReportBtn': { en: 'Complete Report', th: 'เสร็จสิ้นรายงาน' },
  'apr.noRequests': { en: 'No appraisal requests found.', th: 'ไม่พบคำขอประเมิน' },

  // Quotation builder
  'qt.title': { en: 'Quotation Builder', th: 'สร้างใบเสนอราคา' },
  'qt.subtitle': {
    en: 'Build and print a formatted quote from selected diamonds',
    th: 'สร้างและพิมพ์ใบเสนอราคาจากเพชรที่เลือก',
  },
  'qt.preview': { en: 'Preview', th: 'ตัวอย่าง' },
  'qt.editMode': { en: 'Edit Mode', th: 'โหมดแก้ไข' },
  'qt.printPdf': { en: 'Print / PDF', th: 'พิมพ์ / PDF' },
  'qt.quoteDetails': { en: 'Quote Details', th: 'รายละเอียดใบเสนอราคา' },
  'qt.quoteRef': { en: 'Quote Reference', th: 'อ้างอิงใบเสนอราคา' },
  'qt.discountPct': { en: 'Discount %', th: 'ส่วนลด %' },
  'qt.selectCustomer': { en: 'Select customer…', th: 'เลือกลูกค้า…' },
  'qt.notesTerms': { en: 'Notes / Terms', th: 'หมายเหตุ / เงื่อนไข' },
  'qt.notesPlaceholder': {
    en: 'Payment terms, special conditions…',
    th: 'เงื่อนไขการชำระเงิน เงื่อนไขพิเศษ…',
  },
  'qt.quoteSummary': { en: 'Quote Summary', th: 'สรุปใบเสนอราคา' },
  'qt.diamondsSelected': { en: 'Diamonds selected', th: 'เพชรที่เลือก' },
  'qt.subtotal': { en: 'Subtotal', th: 'ยอดรวมย่อย' },
  'qt.discount': { en: 'Discount', th: 'ส่วนลด' },
  'qt.total': { en: 'Total', th: 'รวมทั้งสิ้น' },
  'qt.validUntil': { en: 'Valid until:', th: 'ใช้ได้ถึง:' },
  'qt.searchDiamonds': { en: 'Search diamonds…', th: 'ค้นหาเพชร…' },
  'qt.preparedFor': { en: 'Prepared for', th: 'จัดทำให้' },
  'qt.description': { en: 'Description', th: 'รายละเอียด' },
  'qt.cert': { en: 'Cert', th: 'ใบรับรอง' },
  'qt.cut': { en: 'Cut', th: 'การเจียระไน' },
  'qt.priceUsd': { en: 'Price (USD)', th: 'ราคา (USD)' },
  'qt.notesAndTerms': { en: 'Notes & Terms', th: 'หมายเหตุและเงื่อนไข' },
  'qt.quotation': { en: 'QUOTATION', th: 'ใบเสนอราคา' },
  'qt.date': { en: 'Date:', th: 'วันที่:' },
  'qt.validDays': {
    en: 'This quotation is valid for {d} days from the date of issue. Prices are subject to availability. All prices in USD.',
    th: 'ใบเสนอราคานี้มีผล {d} วันนับจากวันออก ราคาขึ้นอยู่กับความพร้อม ราคาทั้งหมดเป็นดอลลาร์สหรัฐ',
  },

  // Rapaport calculator
  'rap.title': { en: 'Rapaport Price Calculator', th: 'เครื่องคิดเลขราคา Rapaport' },
  'rap.subtitle': {
    en: 'Estimate diamond value and apply bulk price adjustments',
    th: 'ประเมินมูลค่าเพชรและปรับราคาเป็นชุด',
  },
  'rap.calculator': { en: 'Price Calculator', th: 'เครื่องคิดเลขราคา' },
  'rap.bulkUpdate': { en: 'Bulk Price Update', th: 'ปรับราคาเป็นชุด' },
  'rap.parameters': { en: 'Diamond Parameters', th: 'พารามิเตอร์เพชร' },
  'rap.shape': { en: 'Shape', th: 'รูปทรง' },
  'rap.color': { en: 'Color', th: 'สี' },
  'rap.clarity': { en: 'Clarity', th: 'ความใส' },
  'rap.caratWeight': { en: 'Carat Weight', th: 'น้ำหนักกะรัต' },
  'rap.bracket': { en: 'Bracket:', th: 'ช่วง:' },
  'rap.multiplier': { en: 'multiplier', th: 'ตัวคูณ' },
  'rap.calculate': { en: 'Calculate Price', th: 'คำนวณราคา' },
  'rap.reset': { en: 'Reset', th: 'รีเซ็ต' },
  'rap.estimatedPrice': { en: 'Estimated Price', th: 'ราคาประเมิน' },
  'rap.totalRapPrice': { en: 'Total Rapaport Price', th: 'ราคา Rapaport รวม' },
  'rap.perCarat': { en: '/ carat', th: '/ กะรัต' },
  'rap.formulaBreakdown': { en: 'Formula Breakdown', th: 'รายละเอียดสูตร' },
  'rap.formula': { en: 'Formula', th: 'สูตร' },
  'rap.selectParamsHint': { en: 'Select parameters and click', th: 'เลือกพารามิเตอร์แล้วกด' },
  'rap.colorAdjustments': { en: 'Color Adjustments', th: 'ตัวคูณสี' },
  'rap.caratMultipliers': { en: 'Carat Multipliers', th: 'ตัวคูณกะรัต' },
  'rap.roundPremium': { en: 'adds a', th: 'เพิ่ม' },
  'rap.premium': { en: 'premium to the final price', th: 'ให้ราคาสุดท้าย' },
  'rap.roundShape': { en: 'Round shape', th: 'ทรงกลม' },
  'rap.baseRates': { en: 'Clarity Base Rates ($/ct)', th: 'อัตราพื้นฐานตามความใส ($/กะรัต)' },
  'rap.invalidCarat': {
    en: 'Please enter a valid carat weight greater than 0.',
    th: 'กรุณากรอกน้ำหนักกะรัตที่ถูกต้องมากกว่า 0',
  },
  'rap.maxCarat': { en: 'Carat weight must be 50 or less.', th: 'น้ำหนักกะรัตต้องไม่เกิน 50' },
  'rap.filterSubtitle': {
    en: 'Filter inventory and apply a Rapaport % adjustment. Preview before committing.',
    th: 'กรองสินค้าและปรับราคาตาม Rapaport (%) พร้อมดูตัวอย่างก่อนบันทึก',
  },
  'rap.allShapes': { en: 'All Shapes', th: 'ทุกรูปทรง' },
  'rap.allColors': { en: 'All Colors', th: 'ทุกสี' },
  'rap.allClarities': { en: 'All Clarities', th: 'ทุกความใส' },
  'rap.allStatuses': { en: 'All Statuses', th: 'ทุกสถานะ' },
  'rap.rapAdjustment': { en: 'Rapaport Adjustment (%)', th: 'ปรับราคา Rapaport (%)' },
  'rap.belowRap': { en: '% below Rap', th: '% ต่ำกว่า Rap' },
  'rap.aboveRap': { en: '% above Rap', th: '% สูงกว่า Rap' },
  'rap.atRap': { en: 'At Rap price', th: 'ที่ราคา Rap' },
  'rap.generatePreview': { en: 'Generate Preview', th: 'สร้างตัวอย่าง' },
  'rap.previewTitle': { en: 'Price Update Preview', th: 'ตัวอย่างการปรับราคา' },
  'rap.matched': { en: 'diamonds matched', th: 'เม็ดที่ตรง' },
  'rap.selected': { en: 'selected', th: 'เลือกแล้ว' },
  'rap.valueChange': { en: 'Value change', th: 'มูลค่าที่เปลี่ยน' },
  'rap.commit': { en: 'Commit', th: 'บันทึก' },
  'rap.updates': { en: 'Updates', th: 'รายการ' },
  'rap.currentTotal': { en: 'Current Total Value', th: 'มูลค่ารวมเดิม' },
  'rap.newTotal': { en: 'New Total Value', th: 'มูลค่ารวมใหม่' },
  'rap.netChange': { en: 'Net Change', th: 'ผลต่างสุทธิ' },
  'rap.currentPrice': { en: 'Current Price', th: 'ราคาปัจจุบัน' },
  'rap.rapBase': { en: 'Rap Base', th: 'ราคาพื้นฐาน Rap' },
  'rap.newPrice': { en: 'New Price', th: 'ราคาใหม่' },
  'rap.diff': { en: 'Diff', th: 'ผลต่าง' },
  'rap.noMatch': { en: 'No diamonds match the selected filters.', th: 'ไม่มีเพชรที่ตรงกับตัวกรอง' },
  'rap.bulkHint': {
    en: 'Set filters and adjustment, then click Generate Preview',
    th: 'ตั้งค่าตัวกรองและเปอร์เซ็นต์ แล้วกดสร้างตัวอย่าง',
  },
  'rap.bulkHintSub': {
    en: 'You can review and deselect individual diamonds before committing',
    th: 'คุณสามารถตรวจสอบและยกเลิกเลือกรายการก่อนบันทึกได้',
  },
  'rap.successUpdated': { en: 'Successfully updated prices for', th: 'อัปเดตราคาเรียบร้อยแล้ว' },
  'rap.diamonds': { en: 'diamonds.', th: 'เม็ด' },

  // Accounting
  'acc.title': { en: 'Accounting', th: 'บัญชี' },
  'acc.subtitle': {
    en: 'Revenue, expenses, P&L reports, tax summaries & AI-powered analysis',
    th: 'รายรับ รายจ่าย รายงานกำไรขาดทุน สรุปภาษี และการวิเคราะห์ด้วย AI',
  },
  'acc.totalRevenue': { en: 'Total Revenue', th: 'รายได้รวม' },
  'acc.totalExpenses': { en: 'Total Expenses', th: 'รายจ่ายรวม' },
  'acc.netProfit': { en: 'Net Profit', th: 'กำไรสุทธิ' },
  'acc.profitMargin': { en: 'Profit Margin', th: 'อัตรากำไร' },
  'acc.transactions': { en: 'transactions', th: 'รายการ' },
  'acc.afterExpenses': { en: 'After all expenses', th: 'หลังหักค่าใช้จ่ายทั้งหมด' },
  'acc.grossMarginRate': { en: 'Gross margin rate', th: 'อัตรากำไรขั้นต้น' },
  'acc.overview': { en: 'Overview', th: 'ภาพรวม' },
  'acc.plReport': { en: 'P&L Report', th: 'รายงานกำไรขาดทุน' },
  'acc.taxSummary': { en: 'Tax Summary', th: 'สรุปภาษี' },
  'acc.aiAnalysis': { en: 'AI Analysis', th: 'วิเคราะห์ด้วย AI' },
  'acc.aiInvoice': { en: 'AI Invoice Analysis', th: 'วิเคราะห์ใบแจ้งหนี้ด้วย AI' },
  'acc.analyzing': { en: 'Analyzing…', th: 'กำลังวิเคราะห์…' },
  'acc.monthlyRevExp': { en: 'Monthly Revenue vs Expenses', th: 'รายรับ vs รายจ่ายรายเดือน' },
  'acc.ytdPerformance': { en: 'year-to-date performance', th: 'ผลงานตั้งแต่ต้นปี' },
  'acc.expenses': { en: 'Expenses', th: 'รายจ่าย' },
  'acc.expenseBreakdown': { en: 'Expense Breakdown', th: 'แยกประเภทรายจ่าย' },
  'acc.revenueSources': { en: 'Revenue Sources', th: 'แหล่งรายได้' },
  'acc.excellent': { en: 'Excellent (>60%)', th: 'ดีเยี่ยม (>60%)' },
  'acc.avgTransaction': { en: 'Avg Transaction', th: 'เฉลี่ยต่อรายการ' },
  'acc.flaggedItems': { en: 'Flagged Items', th: 'รายการที่ถูกแจ้ง' },
  'acc.pending': { en: 'Pending', th: 'รอดำเนินการ' },
  'acc.cleared': { en: 'Cleared', th: 'ยืนยันแล้ว' },
  'acc.bankRecon': { en: 'Bank Reconciliation Summary', th: 'สรุปการกระทบยอดธนาคาร' },
  'acc.clearedRevenue': { en: 'Cleared Revenue', th: 'รายรับที่ยืนยันแล้ว' },
  'acc.clearedExpenses': { en: 'Cleared Expenses', th: 'รายจ่ายที่ยืนยันแล้ว' },
  'acc.confirmedReceipts': { en: 'Confirmed receipts', th: 'รับชำระยืนยันแล้ว' },
  'acc.confirmedPayments': { en: 'Confirmed payments', th: 'ชำระเงินยืนยันแล้ว' },
  'acc.awaitingConfirmation': { en: 'Awaiting confirmation', th: 'รอการยืนยัน' },
  'acc.requiresReview': { en: 'Requires review', th: 'ต้องตรวจสอบ' },
  'acc.description': { en: 'Description', th: 'รายละเอียด' },
  'acc.category': { en: 'Category', th: 'หมวดหมู่' },
  'acc.taxCategory': { en: 'Tax Category', th: 'หมวดภาษี' },
  'acc.amount': { en: 'Amount', th: 'จำนวนเงิน' },
  'acc.period': { en: 'Period', th: 'ช่วงเวลา' },
  'acc.revenue': { en: 'Revenue', th: 'รายได้' },
  'acc.cogs': { en: 'COGS', th: 'ต้นทุนขาย' },
  'acc.grossProfit': { en: 'Gross Profit', th: 'กำไรขั้นต้น' },
  'acc.opExpenses': { en: 'Op. Expenses', th: 'ค่าใช้จ่ายดำเนินงาน' },
  'acc.margin': { en: 'Margin', th: 'อัตรากำไร' },
  'acc.ytdTotal': { en: 'YTD Total', th: 'รวมตั้งแต่ต้นปี' },
  'acc.monthly': { en: 'Monthly', th: 'รายเดือน' },
  'acc.quarterly': { en: 'Quarterly', th: 'รายไตรมาส' },
  'acc.plStatement': { en: 'Profit & Loss Statement', th: 'งบกำไรขาดทุน' },
  'acc.netProfitTrend': { en: 'Net Profit Trend', th: 'แนวโน้มกำไรสุทธิ' },
  'acc.exportCsv': { en: 'Export CSV', th: 'ส่งออก CSV' },
  'acc.exportPdf': { en: 'Export PDF', th: 'ส่งออก PDF' },

  // Analytics
  'ana.title': { en: 'Analytics', th: 'วิเคราะห์' },
  'ana.subtitle': { en: 'Live inventory and customer insights', th: 'ข้อมูลสดของสินค้าและลูกค้า' },
  'ana.totalInventoryValue': { en: 'TOTAL INVENTORY VALUE', th: 'มูลค่าสินค้ารวม' },
  'ana.totalSalesRevenue': { en: 'TOTAL SALES REVENUE', th: 'รายได้จากการขายรวม' },
  'ana.avgCaratWeight': { en: 'AVG CARAT WEIGHT', th: 'น้ำหนักเฉลี่ย (กะรัต)' },
  'ana.topCustomer': { en: 'TOP CUSTOMER', th: 'ลูกค้าอันดับ 1' },
  'ana.acrossInventory': { en: 'across all inventory', th: 'จากสินค้าทั้งหมด' },
  'ana.total': { en: 'total', th: 'รวม' },
  'ana.noCustomersYet': { en: 'no customers yet', th: 'ยังไม่มีลูกค้า' },
  'ana.clarityDistribution': { en: 'Clarity Distribution', th: 'การกระจายความใส' },
  'ana.byClarityGrade': { en: 'Diamond count by clarity grade', th: 'จำนวนเพชรตามระดับความใส' },
  'ana.shapeDistribution': { en: 'Shape Distribution', th: 'การกระจายรูปทรง' },
  'ana.byShape': { en: 'Inventory count by diamond shape', th: 'จำนวนสินค้าตามรูปทรง' },
  'ana.topCustomersVolume': {
    en: 'Top Customers by Purchase Volume',
    th: 'ลูกค้าอันดับสูงตามยอดซื้อ',
  },
  'ana.rankedByPurchases': {
    en: 'Ranked by total purchases (USD)',
    th: 'จัดอันดับตามยอดซื้อรวม (USD)',
  },
  'ana.locationDistribution': { en: 'Location Distribution', th: 'การกระจายตามคลัง' },
  'ana.byVaultLocation': { en: 'Diamond count by vault location', th: 'จำนวนเพชรตามตำแหน่งในคลัง' },
  'ana.diamonds': { en: 'diamonds', th: 'เม็ด' },

  // AI assistant
  'ai.title': { en: 'Glanz AI Assistant', th: 'ผู้ช่วย AI Glanz' },
  'ai.subtitle': {
    en: 'Diamond recommendations · Customer intelligence · Sales automation',
    th: 'แนะนำเพชร · ข้อมูลลูกค้า · ระบบอัตโนมัติฝ่ายขาย',
  },
  'ai.poweredBy': { en: 'Powered by OpenAI', th: 'ขับเคลื่อนด้วย OpenAI' },
  'ai.clearChat': { en: 'Clear chat', th: 'ล้างการสนทนา' },
  'ai.welcome': { en: 'How can I help you today?', th: 'วันนี้มีอะไรให้ช่วยไหม?' },
  'ai.welcomeSub': {
    en: "Ask me anything about diamonds, customers, pricing, or sales strategy. I'm here to help you close more deals.",
    th: 'ถามเรื่องเพชร ลูกค้า ราคา หรือกลยุทธ์การขายได้เลย เราพร้อมช่วยให้คุณปิดการขายได้มากขึ้น',
  },
  'ai.inputPlaceholder': {
    en: 'Ask about diamond recommendations, customer queries, pricing strategy...',
    th: 'ถามเรื่องการแนะนำเพชร คำถามลูกค้า กลยุทธ์ราคา...',
  },
  'ai.pressEnter': { en: 'Press', th: 'กด' },
  'ai.toSend': { en: 'to send ·', th: 'เพื่อส่ง ·' },
  'ai.forNewLine': { en: 'for new line', th: 'สำหรับขึ้นบรรทัดใหม่' },
  'ai.qp1': { en: 'Diamond Recommendation', th: 'แนะนำเพชร' },
  'ai.qp1p': {
    en: 'A customer has a budget of $5,000 and wants a round brilliant diamond for an engagement ring. What should I recommend?',
    th: 'ลูกค้ามีงบ $5,000 ต้องการเพชรทรงกลมสำหรับแหวนหมั้น ควรแนะนำอะไร?',
  },
  'ai.qp2': { en: 'Lead Qualification', th: 'คัดกรองลูกค้า' },
  'ai.qp2p': {
    en: "Help me qualify a new lead who is interested in a diamond necklace but hasn't specified a budget yet. What questions should I ask?",
    th: 'ช่วยคัดกรองลูกค้าใหม่ที่สนใจสร้อยเพชรแต่ยังไม่ระบุงบ ควรถามอะไร?',
  },
  'ai.qp3': { en: 'Pricing Strategy', th: 'กลยุทธ์ราคา' },
  'ai.qp3p': {
    en: 'What are the current market trends for fancy colored diamonds and how should I price my inventory?',
    th: 'แนวโน้มตลาดเพชรสีแฟนซีปัจจุบันเป็นอย่างไร และควรตั้งราคาสินค้าอย่างไร?',
  },
  'ai.qp4': { en: 'Sales Automation', th: 'ระบบอัตโนมัติฝ่ายขาย' },
  'ai.qp4p': {
    en: "Suggest a follow-up sequence for a customer who viewed a 2ct D VS1 round diamond but didn't purchase.",
    th: 'แนะนำลำดับการติดตามลูกค้าที่ดูเพชรทรงกลม 2ct D VS1 แต่ยังไม่ซื้อ',
  },
  'ai.qp5': { en: 'Customer Query', th: 'คำถามลูกค้า' },
  'ai.qp5p': {
    en: 'A customer is asking about the difference between GIA and IGI certificates. How should I explain this?',
    th: 'ลูกค้าถามความแตกต่างระหว่างใบรับรอง GIA และ IGI ควรอธิบายอย่างไร?',
  },
  'ai.qp6': { en: 'Inventory Advice', th: 'คำแนะนำสินค้า' },
  'ai.qp6p': {
    en: 'I have slow-moving fancy shape diamonds (pear, marquise) in my inventory. What strategies can I use to move them?',
    th: 'มีเพชรทรงแฟนซี (pear, marquise) ขยับช้า ควรใช้กลยุทธ์อะไร?',
  },

  // Settings
  'set.title': { en: 'Settings', th: 'การตั้งค่า' },
  'set.subtitle': {
    en: 'Manage your system preferences, integrations, and data',
    th: 'จัดการการตั้งค่าระบบ การเชื่อมต่อ และข้อมูล',
  },
  'set.saveChanges': { en: 'Save Changes', th: 'บันทึกการเปลี่ยนแปลง' },
  'set.reset': { en: 'Reset', th: 'รีเซ็ต' },
  'set.discard': { en: 'Discard', th: 'ยกเลิก' },
  'set.unsaved': { en: 'You have unsaved changes', th: 'คุณมีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก' },
  'set.allSaved': { en: 'All changes saved', th: 'บันทึกการเปลี่ยนแปลงทั้งหมดแล้ว' },
  'set.groupGeneral': { en: 'General', th: 'ทั่วไป' },
  'set.groupAppearance': { en: 'Appearance', th: 'รูปลักษณ์' },
  'set.groupFinance': { en: 'Finance', th: 'การเงิน' },
  'set.groupIntegrations': { en: 'Integrations', th: 'การเชื่อมต่อ' },
  'set.groupData': { en: 'Data', th: 'ข้อมูล' },
  'set.groupLicence': { en: 'Licence', th: 'ใบอนุญาต' },
  'set.companyInfo': { en: 'Company Info', th: 'ข้อมูลบริษัท' },
  'set.companyInfoDesc': { en: 'Name, address, VAT number', th: 'ชื่อ ที่อยู่ เลขภาษี' },
  'set.currencyExchange': { en: 'Currency & Exchange', th: 'สกุลเงินและอัตราแลกเปลี่ยน' },
  'set.currencyDesc': { en: 'USD/THB rate, persistence', th: 'อัตรา USD/THB การบันทึกค่า' },
  'set.systemDefaults': { en: 'System Defaults', th: 'ค่าเริ่มต้นระบบ' },
  'set.systemDesc': { en: 'Expiry rules, page size', th: 'กฎหมดอายุ ขนาดหน้า' },
  'set.displayLang': { en: 'Display & Language', th: 'การแสดงผลและภาษา' },
  'set.displayDesc': { en: 'Language, warnings, auto-save', th: 'ภาษา การเตือน บันทึกอัตโนมัติ' },
  'set.theme': { en: 'Theme', th: 'ธีม' },
  'set.themeDesc': { en: 'Dark / light mode', th: 'โหมดมืด / สว่าง' },
  'set.vatTaxRates': { en: 'VAT / Tax Rates', th: 'อัตรา VAT / ภาษี' },
  'set.taxDesc': { en: 'Rates per country & tier', th: 'อัตราตามประเทศและระดับลูกค้า' },
  'set.emailAccounts': { en: 'Email Accounts', th: 'บัญชีอีเมล' },
  'set.emailDesc': { en: 'Gmail, Outlook, SMTP', th: 'Gmail, Outlook, SMTP' },
  'set.dbExport': { en: 'DB Export', th: 'ส่งออกฐานข้อมูล' },
  'set.exportDesc': { en: 'JSON / CSV backup', th: 'สำรองข้อมูล JSON / CSV' },
  'set.licenceActivation': { en: 'Licence & Activation', th: 'ใบอนุญาตและการเปิดใช้งาน' },
  'set.licenceDesc': { en: 'Demo mode, yearly licence', th: 'โหมดทดลอง ใบอนุญาตรายปี' },
  'set.companyInformation': { en: 'Company Information', th: 'ข้อมูลบริษัท' },
  'set.companyInfoUsed': {
    en: 'Used on invoices and printed documents',
    th: 'ใช้ในใบแจ้งหนี้และเอกสารพิมพ์',
  },
  'set.companyName': { en: 'Company Name', th: 'ชื่อบริษัท' },
  'set.companyAddress': { en: 'Company Address', th: 'ที่อยู่บริษัท' },
  'set.vatNumber': { en: 'VAT / Tax Number', th: 'เลขประจำตัวผู้เสียภาษี' },
  'set.breadcrumb': { en: 'Settings', th: 'การตั้งค่า' },

  // Follow-up tasks
  'fut.title': { en: 'Follow-Up Tasks', th: 'งานติดตาม' },
  'fut.editTask': { en: 'Edit Task', th: 'แก้ไขงาน' },
  'fut.newFollowUpTask': { en: 'New Follow-Up Task', th: 'สร้างงานติดตามใหม่' },
  'fut.taskTitle': { en: 'Task Title', th: 'ชื่องาน' },
  'fut.taskTitlePlaceholder': {
    en: 'e.g. Send updated price list',
    th: 'เช่น ส่งราคาที่อัปเดตแล้ว',
  },
  'fut.description': { en: 'Description', th: 'รายละเอียด' },
  'fut.descriptionPlaceholder': {
    en: 'Add details about this task...',
    th: 'เพิ่มรายละเอียดของงาน...',
  },
  'fut.priority': { en: 'Priority', th: 'ความสำคัญ' },
  'fut.status': { en: 'Status', th: 'สถานะ' },
  'fut.assignedTo': { en: 'Assigned To', th: 'ผู้รับผิดชอบ' },
  'fut.dueDate': { en: 'Due Date', th: 'กำหนดเสร็จ' },
  'fut.linkTo': { en: 'Link To', th: 'เชื่อมโยงไปยัง' },
  'fut.none': { en: 'None', th: 'ไม่มี' },
  'fut.deal': { en: 'Deal', th: 'ดีล' },
  'fut.customer': { en: 'Customer', th: 'ลูกค้า' },
  'fut.date': { en: 'Date', th: 'วันที่' },
  'fut.noLink': { en: 'No Link', th: 'ไม่เชื่อมโยง' },
  'fut.selectDeal': { en: 'Select deal...', th: 'เลือกดีล...' },
  'fut.selectCustomer': { en: 'Select customer...', th: 'เลือกลูกค้า...' },
  'fut.tags': { en: 'Tags', th: 'แท็ก' },
  'fut.addTagPlaceholder': { en: 'Add tag and press Enter', th: 'เพิ่มแท็กแล้วกด Enter' },
  'fut.pending': { en: 'Pending', th: 'รอดำเนินการ' },
  'fut.inProgress': { en: 'In Progress', th: 'กำลังดำเนินการ' },
  'fut.saveChanges': { en: 'Save Changes', th: 'บันทึกการเปลี่ยนแปลง' },
  'fut.createTask': { en: 'Create Task', th: 'สร้างงาน' },
  'fut.completedOn': { en: 'Completed', th: 'เสร็จสิ้นเมื่อ' },
  'fut.noTasksFound': { en: 'No tasks found', th: 'ไม่พบงาน' },
  'fut.createOrAdjust': {
    en: 'Create a new task or adjust your filters',
    th: 'สร้างงานใหม่หรือปรับตัวกรอง',
  },
  'fut.noTasks': { en: 'No tasks', th: 'ไม่มีงาน' },
  'fut.clear': { en: 'Clear', th: 'ล้าง' },
  'fut.subtitle': {
    en: 'Track tasks linked to deals, customers, and dates',
    th: 'ติดตามงานที่เชื่อมโยงกับดีล ลูกค้า และกำหนดเวลา',
  },
  'fut.newTask': { en: 'New Task', th: 'สร้างงานใหม่' },
  'fut.totalTasks': { en: 'Total Tasks', th: 'งานทั้งหมด' },
  'fut.completed': { en: 'Completed', th: 'เสร็จสิ้น' },
  'fut.overdue': { en: 'Overdue', th: 'เกินกำหนด' },
  'fut.highPriority': { en: 'High Priority', th: 'ความสำคัญสูง' },
  'fut.searchTasks': { en: 'Search tasks...', th: 'ค้นหางาน...' },
  'fut.list': { en: 'List', th: 'รายการ' },
  'fut.board': { en: 'Board', th: 'บอร์ด' },
  'fut.allPriorities': { en: 'All Priorities', th: 'ทุกความสำคัญ' },
  'fut.allStatuses': { en: 'All Statuses', th: 'ทุกสถานะ' },
  'fut.allOwners': { en: 'All Owners', th: 'ทุกผู้รับผิดชอบ' },
  'fut.allLinks': { en: 'All Links', th: 'ทุกการเชื่อมโยง' },
  'fut.high': { en: 'High', th: 'สูง' },
  'fut.medium': { en: 'Medium', th: 'กลาง' },
  'fut.low': { en: 'Low', th: 'ต่ำ' },

  // Shared / common
  'common.loading': { en: 'Loading…', th: 'กำลังโหลด…' },
  'common.error': { en: 'Something went wrong', th: 'เกิดข้อผิดพลาด' },
  'common.retry': { en: 'Retry', th: 'ลองอีกครั้ง' },
  'common.save': { en: 'Save', th: 'บันทึก' },
  'common.cancel': { en: 'Cancel', th: 'ยกเลิก' },
  'common.delete': { en: 'Delete', th: 'ลบ' },
  'common.edit': { en: 'Edit', th: 'แก้ไข' },
  'common.search': { en: 'Search…', th: 'ค้นหา…' },
  'common.searchEverything': { en: 'Search everything…', th: 'ค้นหาทุกอย่าง…' },
  'common.noResults': { en: 'No results', th: 'ไม่พบผลลัพธ์' },
  'common.new': { en: 'New', th: 'สร้างใหม่' },
  'common.add': { en: 'Add', th: 'เพิ่ม' },
  'common.close': { en: 'Close', th: 'ปิด' },
  'common.confirm': { en: 'Confirm', th: 'ยืนยัน' },
  'common.status': { en: 'Status', th: 'สถานะ' },
  'common.customer': { en: 'Customer', th: 'ลูกค้า' },
  'common.total': { en: 'Total', th: 'รวม' },
  'common.date': { en: 'Date', th: 'วันที่' },
  'common.actions': { en: 'Actions', th: 'จัดการ' },

  // Profile page
  'profile.logout': { en: 'Logout', th: 'ออกจากระบบ' },
  'profile.nav.account': { en: 'Account Info', th: 'ข้อมูลบัญชี' },
  'profile.nav.security': { en: 'Security', th: 'ความปลอดภัย' },
  'profile.nav.appearance': { en: 'Appearance', th: 'การแสดงผล' },
  'profile.nav.notifications': { en: 'Notifications', th: 'การแจ้งเตือน' },
  'profile.accountInfo': { en: 'Account Information', th: 'ข้อมูลบัญชี' },
  'profile.accountInfoSub': {
    en: 'Your profile details and role assignment',
    th: 'รายละเอียดโปรไฟล์และบทบาทของคุณ',
  },
  'profile.displayName': { en: 'Display Name', th: 'ชื่อที่แสดง' },
  'profile.email': { en: 'Email Address', th: 'ที่อยู่อีเมล' },
  'profile.emailLocked': {
    en: 'Email cannot be changed in demo mode',
    th: 'ไม่สามารถเปลี่ยนอีเมลในโหมดสาธิตได้',
  },
  'profile.accountCreated': { en: 'Account Created', th: 'สร้างบัญชีเมื่อ' },
  'profile.rolePerms': { en: 'Role & Permissions', th: 'บทบาทและสิทธิ์' },
  'profile.role.owner': {
    en: 'Full access to all modules, pricing, and system settings',
    th: 'เข้าถึงทุกโมดูล ราคา และการตั้งค่าระบบได้ทั้งหมด',
  },
  'profile.role.manager': {
    en: 'Access to inventory, sales, CRM, and analytics',
    th: 'เข้าถึงคลังสินค้า ยอดขาย CRM และการวิเคราะห์',
  },
  'profile.role.appraiser': {
    en: 'View-only access to inventory, memos, holds, and certificates',
    th: 'ดูอย่างเดียวสำหรับคลังสินค้า ใบเมโม การจอง และใบรับรอง',
  },
  'profile.saveChanges': { en: 'Save Changes', th: 'บันทึกการเปลี่ยนแปลง' },
  'profile.saved': { en: 'Profile updated successfully', th: 'อัปเดตโปรไฟล์สำเร็จแล้ว' },
  'profile.changePassword': { en: 'Change Password', th: 'เปลี่ยนรหัสผ่าน' },
  'profile.passwordSub': {
    en: 'Use a strong password with letters, numbers, and symbols',
    th: 'ใช้รหัสผ่านที่แข็งแรง ผสมตัวอักษร ตัวเลข และสัญลักษณ์',
  },
  'profile.currentPassword': { en: 'Current Password', th: 'รหัสผ่านปัจจุบัน' },
  'profile.currentPlaceholder': { en: 'Enter current password', th: 'กรอกรหัสผ่านปัจจุบัน' },
  'profile.newPassword': { en: 'New Password', th: 'รหัสผ่านใหม่' },
  'profile.newPlaceholder': { en: 'Enter new password', th: 'กรอกรหัสผ่านใหม่' },
  'profile.confirmPassword': { en: 'Confirm New Password', th: 'ยืนยันรหัสผ่านใหม่' },
  'profile.confirmPlaceholder': { en: 'Confirm new password', th: 'ยืนยันรหัสผ่านใหม่' },
  'profile.pwdMismatch': { en: 'Passwords do not match', th: 'รหัสผ่านไม่ตรงกัน' },
  'profile.pwdWeak': { en: 'Weak', th: 'อ่อน' },
  'profile.pwdFair': { en: 'Fair', th: 'ปานกลาง' },
  'profile.pwdStrong': { en: 'Strong', th: 'แข็งแรง' },
  'profile.pwdAllRequired': { en: 'All fields are required.', th: 'กรอกข้อมูลครบทุกช่อง' },
  'profile.pwdMismatchMsg': { en: 'New passwords do not match.', th: 'รหัสผ่านใหม่ไม่ตรงกัน' },
  'profile.pwdTooShort': {
    en: 'Password must be at least 8 characters.',
    th: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร',
  },
  'profile.pwdUpdated': { en: 'Password updated successfully.', th: 'อัปเดตรหัสผ่านสำเร็จแล้ว' },
  'profile.updatePassword': { en: 'Update Password', th: 'อัปเดตรหัสผ่าน' },
  'profile.twoFA': { en: 'Two-Factor Authentication', th: 'การยืนยันตัวตนสองชั้น' },
  'profile.twoFASub': {
    en: 'Add an extra layer of security with an authenticator app',
    th: 'เพิ่มความปลอดภัยด้วยแอปยืนยันตัวตน',
  },
  'profile.enabled': { en: 'Enabled', th: 'เปิดใช้งาน' },
  'profile.disabled': { en: 'Disabled', th: 'ปิดใช้งาน' },
  'profile.twoFAStep1': { en: 'Step 1 — Scan QR Code', th: 'ขั้นตอนที่ 1 — สแกน QR Code' },
  'profile.twoFAScanSub': {
    en: 'Open your authenticator app (Google Authenticator, Authy, etc.) and scan this code',
    th: 'เปิดแอปยืนยันตัวตน (Google Authenticator, Authy ฯลฯ) แล้วสแกนโค้ดนี้',
  },
  'profile.manualKey': { en: 'Manual key:', th: 'คีย์สำรอง:' },
  'profile.twoFAStep2': {
    en: 'Step 2 — Enter Verification Code',
    th: 'ขั้นตอนที่ 2 — กรอกรหัสยืนยัน',
  },
  'profile.verifyEnable': { en: 'Verify & Enable', th: 'ยืนยันและเปิดใช้งาน' },
  'profile.twoFADisabled': {
    en: 'Two-factor authentication disabled.',
    th: 'ปิดการยืนยันตัวตนสองชั้นแล้ว',
  },
  'profile.twoFAEnabledMsg': {
    en: 'Two-factor authentication enabled successfully.',
    th: 'เปิดการยืนยันตัวตนสองชั้นสำเร็จแล้ว',
  },
  'profile.twoFAInvalid': {
    en: 'Invalid code. Please try again.',
    th: 'รหัสไม่ถูกต้อง กรุณาลองอีกครั้ง',
  },
  'profile.twoFAProtected': {
    en: 'Your account is protected with two-factor authentication',
    th: 'บัญชีของคุณได้รับการปกป้องด้วยการยืนยันตัวตนสองชั้น',
  },
  'profile.appearance': { en: 'Appearance', th: 'การแสดงผล' },
  'profile.appearanceSub': {
    en: 'Customize the look and feel of your workspace',
    th: 'ปรับแต่งหน้าตาของพื้นที่ทำงาน',
  },
  'profile.theme': { en: 'Theme', th: 'ธีม' },
  'profile.theme.dark': { en: 'Dark', th: 'มืด' },
  'profile.theme.light': { en: 'Light', th: 'สว่าง' },
  'profile.theme.system': { en: 'System', th: 'ตามระบบ' },
  'profile.themeSystemSub': {
    en: 'Follows your OS dark/light mode setting',
    th: 'ตามโหมดมืด/สว่างของระบบปฏิบัติการ',
  },
  'profile.themeActive': { en: 'theme is active', th: 'กำลังใช้ธีม' },
  'profile.accentColor': { en: 'Accent Color', th: 'สีเน้น' },
  'profile.selected': { en: 'Selected:', th: 'ที่เลือก:' },
  'profile.density': { en: 'Interface Density', th: 'ความหนาแน่นของหน้าจอ' },
  'profile.density.compact': { en: 'Compact', th: 'กระชับ' },
  'profile.density.default': { en: 'Default', th: 'ปกติ' },
  'profile.density.comfortable': { en: 'Comfortable', th: 'หย่อน' },
  'profile.notifPrefs': { en: 'Notification Preferences', th: 'การตั้งค่าการแจ้งเตือน' },
  'profile.notifPrefsSub': {
    en: 'Choose which alerts and updates you want to receive',
    th: 'เลือกการแจ้งเตือนและอัปเดตที่ต้องการรับ',
  },
  'profile.inAppAlerts': { en: 'In-App Alerts', th: 'การแจ้งเตือนในแอป' },
  'profile.notif.inventoryAlerts': { en: 'Inventory Alerts', th: 'แจ้งเตือนคลังสินค้า' },
  'profile.notif.inventoryAlertsDesc': {
    en: 'Low stock, new arrivals, and status changes',
    th: 'สต๊อกต่ำ สินค้าใหม่ และการเปลี่ยนสถานะ',
  },
  'profile.notif.salesUpdates': { en: 'Sales Updates', th: 'อัปเดตยอดขาย' },
  'profile.notif.salesUpdatesDesc': {
    en: 'New sales, payment confirmations, and cancellations',
    th: 'ยอดขายใหม่ การยืนยันชำระเงิน และการยกเลิก',
  },
  'profile.notif.holdExpiry': { en: 'Hold Expiry Reminders', th: 'แจ้งเตือนการจองใกล้หมดอายุ' },
  'profile.notif.holdExpiryDesc': {
    en: 'Alerts when holds are about to expire',
    th: 'แจ้งเตือนเมื่อการจองใกล้หมดอายุ',
  },
  'profile.notif.certExpiry': { en: 'Certificate Expiry', th: 'ใบรับรองใกล้หมดอายุ' },
  'profile.notif.certExpiryDesc': {
    en: 'Warnings when certificates are nearing expiry',
    th: 'เตือนเมื่อใบรับรองใกล้หมดอายุ',
  },
  'profile.notif.memoReminders': { en: 'Memo Reminders', th: 'แจ้งเตือนใบเมโม' },
  'profile.notif.memoRemindersDesc': {
    en: 'Follow-up reminders for outstanding memos',
    th: 'แจ้งเตือนติดตามใบเมโมที่ค้างอยู่',
  },
  'profile.notif.systemAnnouncements': { en: 'System Announcements', th: 'ประกาศจากระบบ' },
  'profile.notif.systemAnnouncementsDesc': {
    en: 'Platform updates and maintenance notices',
    th: 'อัปเดตแพลตฟอร์มและแจ้งซ่อมบำรุง',
  },
  'profile.deliveryChannels': { en: 'Delivery Channels', th: 'ช่องทางการรับข้อมูล' },
  'profile.notif.emailDigest': { en: 'Email Digest', th: 'สรุปทางอีเมล' },
  'profile.notif.emailDigestDesc': {
    en: 'Daily summary of activity sent to your email',
    th: 'สรุปกิจกรรมรายวันส่งถึงอีเมลของคุณ',
  },
  'profile.notif.browserPush': {
    en: 'Browser Push Notifications',
    th: 'การแจ้งเตือนจากเบราว์เซอร์',
  },
  'profile.notif.browserPushDesc': {
    en: 'Real-time push notifications in your browser',
    th: 'แจ้งเตือนแบบทันทีในเบราว์เซอร์ของคุณ',
  },
  'profile.savePrefs': { en: 'Save Preferences', th: 'บันทึกการตั้งค่า' },

  // Accounting: month abbreviations + access badge
  'month.jan': { en: 'Jan', th: 'ม.ค.' },
  'month.feb': { en: 'Feb', th: 'ก.พ.' },
  'month.mar': { en: 'Mar', th: 'มี.ค.' },
  'month.apr': { en: 'Apr', th: 'เม.ย.' },
  'month.may': { en: 'May', th: 'พ.ค.' },
  'month.jun': { en: 'Jun', th: 'มิ.ย.' },
  'month.jul': { en: 'Jul', th: 'ก.ค.' },
  'month.aug': { en: 'Aug', th: 'ส.ค.' },
  'month.sep': { en: 'Sep', th: 'ก.ย.' },
  'month.oct': { en: 'Oct', th: 'ต.ค.' },
  'month.nov': { en: 'Nov', th: 'พ.ย.' },
  'month.dec': { en: 'Dec', th: 'ธ.ค.' },
  'acc.fullAccess': { en: 'Full Access', th: 'เข้าถึงทั้งหมด' },

  // Inventory page
  'inv.title': { en: 'Diamond Inventory', th: 'คลังเพชร' },
  'inv.stones': { en: 'stones', th: 'เม็ด' },
  'inv.totalValue': { en: 'Total value', th: 'มูลค่ารวม' },
  'inv.scan': { en: 'Scan', th: 'สแกน' },
  'inv.scanTitle': {
    en: 'Scan QR / Barcode to find diamond',
    th: 'สแกน QR / บาร์โค้ดเพื่อค้นหาเพชร',
  },
  'inv.addDiamond': { en: 'Add Diamond', th: 'เพิ่มเพชร' },
  'inv.searchPlaceholder': {
    en: 'Search SKU or cert number...',
    th: 'ค้นหา SKU หรือเลขใบรับรอง...',
  },
  'inv.allShapes': { en: 'All Shapes', th: 'ทุกรูปทรง' },
  'inv.allColors': { en: 'All Colors', th: 'ทุกสี' },
  'inv.allClarities': { en: 'All Clarities', th: 'ทุกระดับความใส' },
  'inv.allStatuses': { en: 'All Statuses', th: 'ทุกสถานะ' },
  'inv.allVaults': { en: 'All Vaults', th: 'ทุกตู้เซฟ' },
  'inv.selected': { en: 'selected', th: 'รายการที่เลือก' },
  'inv.deleteSelected': { en: 'Delete selected', th: 'ลบที่เลือก' },
  'inv.clearSelection': { en: 'Clear selection', th: 'ล้างการเลือก' },
  'inv.loading': { en: 'Loading inventory…', th: 'กำลังโหลดคลังเพชร…' },
  'inv.empty': { en: 'No diamonds match your filters', th: 'ไม่พบเพชรที่ตรงกับตัวกรอง' },
  'inv.emptySub': {
    en: 'Try adjusting your search or filter criteria',
    th: 'ลองปรับการค้นหาหรือตัวกรองของคุณ',
  },
  'inv.col.sku': { en: 'SKU', th: 'SKU' },
  'inv.col.shape': { en: 'Shape', th: 'รูปทรง' },
  'inv.col.carat': { en: 'Carat', th: 'กะรัต' },
  'inv.col.color': { en: 'Color', th: 'สี' },
  'inv.col.clarity': { en: 'Clarity', th: 'ความใส' },
  'inv.col.cut': { en: 'Cut', th: 'การเจียระไน' },
  'inv.col.location': { en: 'Location', th: 'ที่เก็บ' },
  'inv.col.status': { en: 'Status', th: 'สถานะ' },
  'inv.col.cost': { en: 'Cost', th: 'ต้นทุน' },
  'inv.col.price': { en: 'Price', th: 'ราคา' },
  'inv.col.cert': { en: 'Cert', th: 'ใบรับรอง' },
  'inv.col.actions': { en: 'Actions', th: 'จัดการ' },
  'inv.editDiamond': { en: 'Edit diamond', th: 'แก้ไขเพชร' },
  'inv.genQr': { en: 'Generate QR code', th: 'สร้าง QR code' },
  'inv.manageImages': { en: 'Manage images', th: 'จัดการรูปภาพ' },
  'inv.deleteDiamond': {
    en: 'Delete diamond — this cannot be undone',
    th: 'ลบเพชร — การกระทำนี้ย้อนกลับไม่ได้',
  },
  'inv.deleteTitle': { en: 'Delete Diamond', th: 'ลบเพชร' },
  'inv.deleteMsg': {
    en: 'from inventory? This action cannot be undone.',
    th: 'ออกจากคลังเพชร? การกระทำนี้ย้อนกลับไม่ได้',
  },

  // Status badge labels (shared across all modules)
  'status.AVAILABLE': { en: 'Available', th: 'พร้อมขาย' },
  'status.ON_MEMO': { en: 'On Memo', th: 'ฝากขาย' },
  'status.ON_HOLD': { en: 'On Hold', th: 'จอง' },
  'status.SOLD': { en: 'Sold', th: 'ขายแล้ว' },
  'status.CERTIFIED': { en: 'Certified', th: 'รับรองแล้ว' },
  'status.ROUGH': { en: 'Rough', th: 'เพชรเถื่อน' },
  'status.POLISHED': { en: 'Polished', th: 'เจียระไนแล้ว' },
  'status.RETURNED': { en: 'Returned', th: 'คืนแล้ว' },
  'status.PAID': { en: 'Paid', th: 'ชำระแล้ว' },
  'status.PENDING': { en: 'Pending', th: 'รอดำเนินการ' },
  'status.OVERDUE': { en: 'Overdue', th: 'เกินกำหนด' },
  'status.ORDERED': { en: 'Ordered', th: 'สั่งซื้อแล้ว' },
  'status.RECEIVED': { en: 'Received', th: 'รับแล้ว' },
  'status.CANCELLED': { en: 'Cancelled', th: 'ยกเลิก' },
  'status.ACTIVE': { en: 'Active', th: 'ใช้งาน' },
  'status.CONVERTED': { en: 'Converted', th: 'แปลงแล้ว' },
  'status.RELEASED': { en: 'Released', th: 'ปล่อยแล้ว' },
  'status.PLATINUM': { en: 'Platinum', th: 'แพลทินัม' },
  'status.GOLD': { en: 'Gold', th: 'ทองคำ' },
  'status.STANDARD': { en: 'Standard', th: 'มาตรฐาน' },
  'status.HIGH': { en: 'High', th: 'สูง' },
  'status.MEDIUM': { en: 'Medium', th: 'กลาง' },
  'status.LOW': { en: 'Low', th: 'ต่ำ' },

  // CRM page
  'crm.title': { en: 'CRM — Suppliers & Customers', th: 'ลูกค้าและผู้จัดส่ง' },
  'crm.suppliers': { en: 'Suppliers', th: 'ผู้จัดส่ง' },
  'crm.customers': { en: 'Customers', th: 'ลูกค้า' },
  'crm.wishlist': { en: 'Wishlist', th: 'รายการโปรด' },
  'crm.rated4': { en: 'rated 4★+', th: 'เรต 4★+' },
  'crm.platinumCount': { en: 'Platinum', th: 'แพลทินัม' },
  'crm.totalPurchases': { en: 'Total Purchases', th: 'ยอดซื้อรวม' },
  'crm.allTimeSpend': { en: 'All-time customer spend', th: 'ยอดใช้จ่ายรวมของลูกค้า' },
  'crm.outstanding': { en: 'Outstanding', th: 'ค้างชำระ' },
  'crm.acrossCustomers': { en: 'Across customers', th: 'จากลูกค้า' },
  'crm.addSupplier': { en: 'Add Supplier', th: 'เพิ่มผู้จัดส่ง' },
  'crm.addCustomer': { en: 'Add Customer', th: 'เพิ่มลูกค้า' },
  'crm.searchCustomer': { en: 'Search customer or company...', th: 'ค้นหาลูกค้าหรือบริษัท...' },
  'crm.searchSupplier': { en: 'Search supplier or code...', th: 'ค้นหาผู้จัดส่งหรือรหัส...' },
  'crm.allTiers': { en: 'All Tiers', th: 'ทุกระดับ' },
  'crm.noCustomers': { en: 'No customers found', th: 'ไม่พบลูกค้า' },
  'crm.noSuppliers': { en: 'No suppliers found', th: 'ไม่พบผู้จัดส่ง' },
  'crm.col.customer': { en: 'Customer', th: 'ลูกค้า' },
  'crm.col.company': { en: 'Company', th: 'บริษัท' },
  'crm.col.tier': { en: 'Tier', th: 'ระดับ' },
  'crm.col.location': { en: 'Location', th: 'ที่ตั้ง' },
  'crm.col.email': { en: 'Email', th: 'อีเมล' },
  'crm.col.totalPurchases': { en: 'Total Purchases', th: 'ยอดซื้อรวม' },
  'crm.col.creditLimit': { en: 'Credit Limit', th: 'วงเงินเครดิต' },
  'crm.col.lastPurchase': { en: 'Last Purchase', th: 'ซื้อล่าสุด' },
  'crm.col.supplier': { en: 'Supplier', th: 'ผู้จัดส่ง' },
  'crm.col.code': { en: 'Code', th: 'รหัส' },
  'crm.col.contact': { en: 'Contact', th: 'ผู้ติดต่อ' },
  'crm.col.rating': { en: 'Rating', th: 'คะแนน' },
  'crm.col.balance': { en: 'Balance', th: 'ยอดคงเหลือ' },
  'crm.purchases': { en: 'Purchases', th: 'ยอดซื้อ' },
  'crm.noOutstanding': { en: 'No outstanding', th: 'ไม่มีค้างชำระ' },
  'crm.editCustomer': { en: 'Edit customer', th: 'แก้ไขลูกค้า' },
  'crm.editSupplier': { en: 'Edit supplier', th: 'แก้ไขผู้จัดส่ง' },
  'crm.purchaseHistory': { en: 'View purchase history', th: 'ดูประวัติการซื้อ' },
  'crm.used': { en: 'used', th: 'ใช้ไป' },

  // Sales page
  'sales.title': { en: 'Sales & Invoices', th: 'การขายและใบแจ้งหนี้' },
  'sales.invoices': { en: 'invoices', th: 'ใบแจ้งหนี้' },
  'sales.newSale': { en: 'New Sale', th: 'บันทึกการขาย' },
  'sales.totalRevenue': { en: 'Total Revenue', th: 'รายได้รวม' },
  'sales.totalMargin': { en: 'Total Margin', th: 'กำไรรวม' },
  'sales.avgMargin': { en: 'avg margin', th: 'กำไรเฉลี่ย' },
  'sales.pendingCollection': { en: 'Pending Collection', th: 'รอรับชำระ' },
  'sales.unpaidInvoices': { en: 'unpaid invoices', th: 'ใบแจ้งหนี้ที่ยังไม่ชำระ' },
  'sales.searchPlaceholder': {
    en: 'Search invoice, customer, SKU...',
    th: 'ค้นหาใบแจ้งหนี้ ลูกค้า SKU...',
  },
  'sales.all': { en: 'All', th: 'ทั้งหมด' },
  'sales.loading': { en: 'Loading sales…', th: 'กำลังโหลดการขาย…' },
  'sales.empty': { en: 'No sales found', th: 'ไม่พบรายการขาย' },
  'sales.emptySub': {
    en: 'Create a new sale to get started',
    th: 'สร้างรายการขายใหม่เพื่อเริ่มต้น',
  },
  'sales.col.invoice': { en: 'Invoice #', th: 'เลขใบแจ้งหนี้' },
  'sales.col.salePrice': { en: 'Sale Price', th: 'ราคาขาย' },
  'sales.col.margin': { en: 'Margin', th: 'กำไร' },
  'sales.col.saleDate': { en: 'Sale Date', th: 'วันที่ขาย' },
  'sales.col.dueDate': { en: 'Due Date', th: 'วันครบกำหนด' },
  'sales.sold': { en: 'Sold', th: 'ขายวันที่' },
  'sales.due': { en: 'Due', th: 'ครบกำหนด' },
  'sales.overdueExcl': { en: 'Overdue!', th: 'เกินกำหนด!' },
  'sales.pay': { en: 'Pay', th: 'ชำระ' },
  'sales.markPaid': { en: 'Mark as paid', th: 'ทำเครื่องหมายว่าชำระแล้ว' },
  'sales.invoice': { en: 'Invoice', th: 'ใบแจ้งหนี้' },
  'sales.genInvoice': { en: 'Generate invoice', th: 'สร้างใบแจ้งหนี้' },
  'sales.merchantCopy': { en: 'Merchant copy', th: 'สำเนาร้านค้า' },
  'sales.markPaidTitle': { en: 'Mark Invoice as Paid', th: 'ยืนยันการชำระเงิน' },
  'sales.confirmPayment': { en: 'Confirm Payment', th: 'ยืนยันการชำระเงิน' },
  'sales.confirmPayMsg': { en: 'Confirm payment received for', th: 'ยืนยันการรับชำระเงินสำหรับ' },
  'sales.from': { en: 'from', th: 'จาก' },

  // Purchases, Holds, Memos (core list pages)
  'purchases.title': { en: 'Purchase Orders', th: 'ใบสั่งซื้อ' },
  'purchases.subtitle': {
    en: 'Track diamond acquisitions from suppliers',
    th: 'ติดตามการรับเพชรจากผู้จัดส่ง',
  },
  'purchases.supplier': { en: 'Suppliers on sheet', th: 'ผู้จัดส่งบนแผ่นใบสั่งซื้อ' },
  'purchases.totalSpent': { en: 'Total Spent', th: 'ยอดใช้จ่ายรวม' },
  'purchases.avgCost': { en: 'Avg Cost', th: 'เฉลี่ยต้นทุน' },
  'purchases.openOrders': { en: 'Open Orders', th: 'ออเดอร์เปิด' },
  'purchases.searchPlaceholder': {
    en: 'Search PO, supplier, SKU...',
    th: 'ค้นหา PO ผู้จัดส่ง SKU...',
  },
  'purchases.purchasedOn': { en: 'Purchased on', th: 'ซื้อเมื่อ' },
  'purchases.payTerm': { en: 'Pay term', th: 'เงื่อนไขการชำระ' },
  'purchases.totalValue': { en: 'Total value', th: 'มูลค่ารวม' },

  'purchases.inTransit': { en: 'in transit', th: 'ระหว่างจัดส่ง' },
  'purchases.openPos': { en: 'open POs', th: 'ใบสั่งซื้อเปิด' },
  'purchases.pOsCompleted': { en: 'POs completed', th: 'ใบสั่งซื้อเสร็จสิ้น' },
  'purchases.pastExpected': { en: 'Past expected date', th: 'เกินวันที่คาดหวัง' },
  'purchases.allSuppliers': { en: 'All Suppliers', th: 'ผู้จัดส่งทั้งหมด' },

  'holds.subtitle': { en: 'Track customer diamond reservations', th: 'ติดตามการจองเพชรของลูกค้า' },

  'memos.title': { en: 'Memos', th: 'ใบเมโม' },
  'memos.subtitle': {
    en: 'Track shipment status, updates, and notes',
    th: 'ติดตามสถานะการจัดส่ง การอัปเดต และโน้ต',
  },
  'memos.shipdate': { en: 'Shipdate', th: 'วันที่จัดส่ง' },
  'memos.shipped': { en: 'Shipped', th: 'จัดส่งแล้ว' },
  'memos.retDate': { en: 'Ret date', th: 'วันที่คืน' },
  'memos.status': { en: 'Status', th: 'สถานะ' },
  'memos.shipmentRef': { en: 'Shipment ref', th: 'อ้างอิงการจัดส่ง' },
  'memos.rma': { en: 'RMA', th: 'RMA' },
  'memos.trackN': { en: 'Track', th: 'ติดตาม' },
  'memos.searchPlaceholder': { en: 'Search memo, customer, SKU...', th: 'ค้นหาเมโม ลูกค้า SKU...' },
  'memos.expired': { en: 'Expired', th: 'หมดอายุ' },
  'memos.follow': { en: 'Followup', th: 'ติดตาม' },
  'memos.dispute': { en: 'Dispute', th: 'โต้แย้ง' },
  'memos.outstanding': { en: 'Outstanding', th: 'ค้างอยู่' },
  'memos.delivered': { en: 'Delivered', th: 'จัดส่งถึง hands' },
  'memos.overdue': { en: 'Overdue', th: 'เกินกำหนด' },
  'memos.memoReasons': { en: 'Memo Reasons', th: 'เหตุผลการจัดทำเมโม' },
  'memos.reasons': { en: 'Reasons', th: 'เหตุผล' },
  'memos.notes': { en: 'Notes', th: 'โน้ต' },
  'memos.export': { en: 'Export', th: 'ส่งออก' },

  'purchases.col.poNumber': { en: 'PO #', th: 'เลข PO' },
  'purchases.col.diamond': { en: 'Diamond SKU', th: 'SKU เพชร' },
  'purchases.col.qty': { en: 'Qty', th: 'จำนวน' },
  'purchases.col.cost': { en: 'Cost', th: 'ต้นทุน' },
  'purchases.col.ordered': { en: 'Order Date', th: 'วันที่สั่ง' },
  'purchases.col.expected': { en: 'Expected', th: 'คาดว่าจะได้' },
  'purchases.col.received': { en: 'Received', th: 'รับเมื่อ' },
  'purchases.totalPos': { en: 'Total POs', th: 'รวมใบสั่งซื้อทั้งหมด' },
  'purchases.pendingOrdered': { en: 'Pending / Ordered', th: 'รอการชำระ / สั่งแล้ว' },
  'purchases.receivedValue': { en: 'Received Value', th: 'ยอดรับสินค้าแล้ว' },
  'purchases.overduePos': { en: 'Overdue POs', th: 'ใบสั่งซื้อเกินกำหนด' },
  'purchases.received': { en: 'marked as received', th: 'บันทึกการรับสินค้าแล้ว' },
  'purchases.receive': { en: 'Receive', th: 'รับสินค้า' },
  'purchases.markReceived': { en: 'Mark as received', th: 'บันทึกการรับสินค้า' },
  'purchases.poDoc': { en: 'PO Doc', th: 'เอกสาร PO' },
  'purchases.printPo': { en: 'Print PO document', th: 'พิมพ์เอกสารใบสั่งซื้อ' },
  'purchases.editPo': { en: 'Edit PO', th: 'แก้ไขใบสั่งซื้อ' },
  'purchases.newestFirst': { en: 'Newest first', th: 'ล่าสุดก่อน' },
  'purchases.oldestFirst': { en: 'Oldest first', th: 'แรกสุดก่อน' },
  'purchases.loading': { en: 'Loading purchase orders…', th: 'กำลังโหลดใบสั่งซื้อ…' },
  'purchases.noOrders': { en: 'No purchase orders found', th: 'ไม่พบใบสั่งซื้อ' },
  'purchases.createFirst': {
    en: 'Create a new PO to track diamond acquisitions',
    th: 'สร้างใบสั่งซื้อใหม่เพื่อติดตามการรับเพชร',
  },
};

export type TFunc = (key: string) => string;

export interface LangCurrencyContextType {
  lang: Lang;
  currency: Currency;
  toggleLang: () => void;
  toggleCurrency: () => void;
  /** Translate a dictionary key in the active language (EN fallback). */
  t: TFunc;
}

const LangCurrencyContext = createContext<LangCurrencyContextType | null>(null);

/** Exported for AutoTranslate (reads active lang without calling the hook). */
export { LangCurrencyContext };

/**
 * Single owner of the language/currency preference. Replaces the old
 * AppLayout cloneElement prop-threading: any component inside AppLayout
 * consumes via useLangCurrency() instead of receiving lang/currency props.
 *
 * Hydration-safe: server render and first client render both use EN/USD,
 * matching exactly; persisted values are applied right after mount — the
 * same pattern AppLayout used for its own state.
 */
export function LangCurrencyProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>('EN');
  const [currency, setCurrency] = useState<Currency>('USD');

  // Restore persisted choices after mount (never during render — avoids
  // hydration mismatch with the server's EN/USD defaults).
  useEffect(() => {
    try {
      const storedLang = window.localStorage.getItem(LANG_KEY);
      if (storedLang === 'EN' || storedLang === 'TH') setLang(storedLang);
      const storedCurrency = window.localStorage.getItem(CURRENCY_KEY);
      if (storedCurrency === 'USD' || storedCurrency === 'THB') setCurrency(storedCurrency);
    } catch {
      /* storage unavailable — keep defaults */
    }
  }, []);

  // Persistence happens inside the user actions below, not in effects: an
  // effect-based persist would fire once on mount with the pre-restore default
  // state and clobber the stored value before the restore effect's update lands.
  const toggleLang = () =>
    setLang((l) => {
      const next: Lang = l === 'EN' ? 'TH' : 'EN';
      try {
        window.localStorage.setItem(LANG_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  const toggleCurrency = () =>
    setCurrency((c) => {
      const next: Currency = c === 'USD' ? 'THB' : 'USD';
      try {
        window.localStorage.setItem(CURRENCY_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });

  const t: TFunc = useCallback(
    (key: string) => {
      const entry = DICT[key];
      if (!entry) return key;
      return lang === 'TH' ? entry.th : entry.en;
    },
    [lang]
  );

  return (
    <LangCurrencyContext.Provider value={{ lang, currency, toggleLang, toggleCurrency, t }}>
      {children}
    </LangCurrencyContext.Provider>
  );
}

/**
 * Consumption hook for language/currency. Only components mounted inside
 * AppLayout may use it (that's where the provider lives); using it elsewhere
 * fails loudly so wiring mistakes surface immediately instead of silently
 * rendering defaults.
 */
export function useLangCurrency(): LangCurrencyContextType {
  const ctx = useContext(LangCurrencyContext);
  if (!ctx) {
    throw new Error('useLangCurrency must be used within AppLayout (LangCurrencyProvider)');
  }
  return ctx;
}

/**
 * Convenience hook for page components: just the translator. Same contract as
 * useLangCurrency — must run inside AppLayout.
 */
export function useT(): TFunc {
  return useLangCurrency().t;
}
